import { useMemo, useState, useCallback } from 'react';
import { Button } from '../../../core/button';
import { ExerciseType } from '@org/model';
import { cn } from '../../../../utils';
import { AlertCircle, CheckCircle2, RotateCcw } from 'lucide-react';
import type { ContentDisplayVariant } from '../../hooks/content-renderer-types';
import type { StudentExerciseContent } from '../../hooks/use-class-blocks';
import type {
  ExerciseFeedbackMessage,
  ExerciseSubmitHandler,
} from '../../hooks/content-renderer-types';
import { ExerciseFeedbackList } from '../../components/shared/exercise-feedback-list';
import { ContentDescription } from '../../components/shared/content-description';
import { renderInlineMarkdown } from '../../../custom/markdown';

interface ScrambleExerciseProps {
  content: StudentExerciseContent;
  onExerciseSubmit?: ExerciseSubmitHandler;
  feedback?: ExerciseFeedbackMessage[];
  variant?: ContentDisplayVariant;
  isReadOnly?: boolean;
}

interface SentenceData {
  sentenceId: string;
  correctSentence: string;
  order: number;
  correction?: string;
}
// Beautiful, vibrant color palette for word cards (reused from matching exercise)
const WORD_COLORS = [
  {
    bg: 'bg-sky-100',
    border: 'border-sky-400',
    text: 'text-sky-700',
    ring: 'ring-sky-300',
    badge: 'bg-sky-500',
    glow: 'shadow-sky-200',
  },
  {
    bg: 'bg-amber-100',
    border: 'border-amber-400',
    text: 'text-amber-700',
    ring: 'ring-amber-300',
    badge: 'bg-amber-500',
    glow: 'shadow-amber-200',
  },
  {
    bg: 'bg-emerald-100',
    border: 'border-emerald-400',
    text: 'text-emerald-700',
    ring: 'ring-emerald-300',
    badge: 'bg-emerald-500',
    glow: 'shadow-emerald-200',
  },
  {
    bg: 'bg-violet-100',
    border: 'border-violet-400',
    text: 'text-violet-700',
    ring: 'ring-violet-300',
    badge: 'bg-violet-500',
    glow: 'shadow-violet-200',
  },
  {
    bg: 'bg-orange-100',
    border: 'border-orange-400',
    text: 'text-orange-700',
    ring: 'ring-orange-300',
    badge: 'bg-orange-500',
    glow: 'shadow-orange-200',
  },
  {
    bg: 'bg-teal-100',
    border: 'border-teal-400',
    text: 'text-teal-700',
    ring: 'ring-teal-300',
    badge: 'bg-teal-500',
    glow: 'shadow-teal-200',
  },
  {
    bg: 'bg-pink-100',
    border: 'border-pink-400',
    text: 'text-pink-700',
    ring: 'ring-pink-300',
    badge: 'bg-pink-500',
    glow: 'shadow-pink-200',
  },
];

type WordEvaluation = { isCorrect: boolean } | null | undefined;

function EvaluationBadge({ evaluation }: { evaluation: WordEvaluation }) {
  if (!evaluation) return null;
  const Icon = evaluation.isCorrect ? CheckCircle2 : AlertCircle;
  const bg = evaluation.isCorrect ? 'bg-emerald-500' : 'bg-rose-500';
  return (
    <span className={cn('absolute -top-1.5 -right-1.5 flex items-center justify-center h-5 w-5 rounded-full shadow-sm', bg)}>
      <Icon className="h-3 w-3 text-white" />
    </span>
  );
}

function getEvaluationRing(evaluation: WordEvaluation): string {
  if (!evaluation) return '';
  return evaluation.isCorrect
    ? 'ring-[3px] ring-emerald-400/60 ring-offset-2'
    : 'ring-[3px] ring-rose-400/60 ring-offset-2 animate-shake';
}

interface WordCardProps {
  word: string;
  colorIndex: number;
  isSelected: boolean;
  isInAnswer: boolean;
  evaluation?: WordEvaluation;
  isReadOnly: boolean;
  onClick: () => void;
}

function WordCard({
  word,
  colorIndex,
  isSelected,
  isInAnswer,
  evaluation,
  isReadOnly,
  onClick,
}: WordCardProps) {
  const color = WORD_COLORS[colorIndex % WORD_COLORS.length];

  const baseStyles = cn(
    'relative flex items-center justify-center gap-1.5 rounded-xl border-2 px-3 py-2 transition-all duration-200 cursor-pointer',
    'min-h-[40px] min-w-[60px]',
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2'
  );

  const colorStyles = cn(color.border, color.bg, color.text, 'shadow-md', color.glow);

  const stateStyles = evaluation
    ? cn(colorStyles, getEvaluationRing(evaluation))
    : isSelected
      ? 'border-primary bg-primary/10 ring-2 ring-primary/30 scale-[1.02] shadow-lg shadow-primary/20 z-10'
      : isInAnswer
        ? cn(colorStyles, 'opacity-50 cursor-not-allowed')
        : cn(colorStyles, 'hover:scale-[1.02] hover:shadow-lg active:scale-[0.98]');

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isReadOnly || isInAnswer}
      className={cn(baseStyles, stateStyles, isReadOnly && 'cursor-default')}
      aria-label={`Word: ${word}`}
    >
      <EvaluationBadge evaluation={evaluation} />
      <span className="text-sm font-medium">{word}</span>
    </button>
  );
}

interface AnswerSlotProps {
  word: string | null;
  position: number;
  colorIndex: number | null;
  evaluation?: WordEvaluation;
  isReadOnly: boolean;
  onClick: () => void;
}

function AnswerSlot({
  word,
  position,
  colorIndex,
  evaluation,
  isReadOnly,
  onClick,
}: AnswerSlotProps) {
  const color = colorIndex !== null ? WORD_COLORS[colorIndex % WORD_COLORS.length] : null;

  const baseStyles = cn(
    'relative flex items-center justify-center rounded-lg border-2 px-3 py-2 transition-all duration-200',
    'min-h-[40px] min-w-[60px]'
  );

  const filledStyles = word && color
    ? cn(
        color.border, color.bg, color.text, 'shadow-md',
        evaluation
          ? getEvaluationRing(evaluation)
          : !isReadOnly && 'active:scale-[0.98]',
        !isReadOnly && 'cursor-pointer hover:opacity-80'
      )
    : 'border-dashed border-slate-300 bg-slate-50 text-slate-400';

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isReadOnly || !word}
      className={cn(baseStyles, filledStyles)}
      aria-label={word ? `Remove word: ${word}` : `Empty slot ${position + 1}`}
    >
      <span className="absolute -top-2 -left-2 flex h-5 w-5 items-center justify-center rounded-full bg-slate-200 text-[10px] font-bold text-slate-600">
        {position + 1}
      </span>
      {word && <EvaluationBadge evaluation={evaluation} />}
      <span className="text-sm font-medium">{word || '___'}</span>
    </button>
  );
}

interface SentenceScrambleProps {
  sentence: SentenceData;
  sentenceIndex: number;
  totalSentences: number;
  userAnswer: string[];
  scrambledWords: { word: string; originalIndex: number; colorIndex: number }[];
  evaluation?: {
    wordEvaluations: Array<{
      position: number;
      expectedWord: string;
      providedWord: string;
      isCorrect: boolean;
    }>;
    isFullyCorrect: boolean;
  } | null;
  isReadOnly: boolean;
  onWordSelect: (word: string) => void;
  onWordRemove: (position: number) => void;
  onReset: () => void;
}

function SentenceScramble({
  sentence,
  sentenceIndex,
  totalSentences,
  userAnswer,
  scrambledWords,
  evaluation,
  isReadOnly,
  onWordSelect,
  onWordRemove,
  onReset,
}: SentenceScrambleProps) {
  const wordsInAnswer = new Set(userAnswer);
  const correctWords = sentence.correctSentence.trim().split(/\s+/);

  // Build evaluation map for answer slots
  const evaluationMap = useMemo(() => {
    if (!evaluation?.wordEvaluations) return new Map<number, { isCorrect: boolean }>();
    const map = new Map<number, { isCorrect: boolean }>();
    for (const wordEval of evaluation.wordEvaluations) {
      map.set(wordEval.position, { isCorrect: wordEval.isCorrect });
    }
    return map;
  }, [evaluation]);

  // Map word to its color index based on original position
  const wordColorMap = useMemo(() => {
    const map = new Map<string, number>();
    scrambledWords.forEach((w) => {
      map.set(w.word, w.colorIndex);
    });
    return map;
  }, [scrambledWords]);

  return (
    <div className="space-y-4">
      {/* Sentence header */}
      {totalSentences > 1 && (
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-slate-700">
            Sentence {sentenceIndex + 1}
          </h4>
          {!isReadOnly && userAnswer.length > 0 && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onReset}
              className="text-slate-500 hover:text-slate-700"
            >
              <RotateCcw className="h-4 w-4 mr-1" />
              Reset
            </Button>
          )}
        </div>
      )}

      {/* Word bank - scrambled words */}
      <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-4">
        <p className="text-xs font-medium text-slate-500 mb-3 text-center">
          Tap words to build the sentence
        </p>
        <div className="flex flex-wrap gap-2 justify-center">
          {scrambledWords.map((wordData, idx) => {
            const isInAnswer = wordsInAnswer.has(wordData.word);
            return (
              <WordCard
                key={`${wordData.word}-${idx}`}
                word={wordData.word}
                colorIndex={wordData.colorIndex}
                isSelected={false}
                isInAnswer={isInAnswer}
                evaluation={null}
                isReadOnly={isReadOnly}
                onClick={() => {
                  if (!isInAnswer) {
                    onWordSelect(wordData.word);
                  }
                }}
              />
            );
          })}
        </div>
      </div>

      {/* Answer area */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <p className="text-xs font-medium text-slate-500 mb-3 text-center">
          Your answer (tap to remove)
        </p>
        <div className="flex flex-wrap gap-2 justify-center min-h-[50px]">
          {correctWords.map((_, position) => {
            const word = userAnswer[position] || null;
            const colorIndex = word ? wordColorMap.get(word) ?? null : null;
            const positionEval = evaluationMap.get(position);

            return (
              <AnswerSlot
                key={position}
                word={word}
                position={position}
                colorIndex={colorIndex}
                evaluation={positionEval}
                isReadOnly={isReadOnly}
                onClick={() => {
                  if (word) {
                    onWordRemove(position);
                  }
                }}
              />
            );
          })}
        </div>
      </div>

      {/* Show correction after evaluation if wrong */}
      {evaluation && !evaluation.isFullyCorrect && sentence.correction && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
          <p className="text-sm text-amber-800">
            <span className="font-medium">Hint:</span> {sentence.correction}
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * Shuffle array using Fisher-Yates algorithm with a seed
 */
function shuffleWithSeed<T>(array: T[], seed: number): T[] {
  const result = [...array];
  let currentIndex = result.length;
  let randomValue = seed;

  // Simple seeded random number generator
  const seededRandom = () => {
    randomValue = (randomValue * 9301 + 49297) % 233280;
    return randomValue / 233280;
  };

  while (currentIndex !== 0) {
    const randomIndex = Math.floor(seededRandom() * currentIndex);
    currentIndex--;
    [result[currentIndex], result[randomIndex]] = [result[randomIndex], result[currentIndex]];
  }

  return result;
}

export function ScrambleExercise({
  content,
  onExerciseSubmit,
  feedback,
  variant = 'card',
  isReadOnly = false,
}: ScrambleExerciseProps) {
  const rawSentences = content.sentences ?? [];
  const sentences: SentenceData[] = useMemo(() => {
    return rawSentences.map((sentence, index) => ({
      sentenceId: sentence.sentenceId ?? `sentence-${index}`,
      correctSentence: sentence.correctSentence ?? '',
      order: sentence.order ?? index,
      correction: sentence.correction ?? undefined,
    }));
  }, [rawSentences]);

  const lastSubmission = useMemo(() => {
    const submission = content.lastSubmission;
    if (submission?.type === ExerciseType.Scramble) {
      return submission;
    }
    return null;
  }, [content.lastSubmission]);

  const lastEvaluationDetails = useMemo(() => {
    const evaluation = (content.lastEvaluation as any) ?? null;
    const autoDetails = (evaluation?.auto as any)?.details ?? evaluation?.details;
    return autoDetails?.kind === 'Scramble' ? autoDetails : null;
  }, [content.lastEvaluation]);

  // Initialize answers from last submission
  const [answers, setAnswers] = useState<string[][]>(() => {
    if (lastSubmission?.answers) {
      return lastSubmission.answers;
    }
    return sentences.map(() => []);
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasModifiedSinceEvaluation, setHasModifiedSinceEvaluation] = useState(false);

  // Generate scrambled words for each sentence
  const scrambledWordsBySentence = useMemo(() => {
    return sentences.map((sentence, sentenceIndex) => {
      const words = sentence.correctSentence.trim().split(/\s+/).filter(w => w.length > 0);
      const wordsWithMeta = words.map((word, originalIndex) => ({
        word,
        originalIndex,
        colorIndex: originalIndex % WORD_COLORS.length,
      }));

      // Use sentence ID or index as seed for consistent shuffling
      const seed = sentence.sentenceId?.charCodeAt(0) ?? sentenceIndex + 42;
      return shuffleWithSeed(wordsWithMeta, seed);
    });
  }, [sentences]);

  const markModified = useCallback(() => {
    if (lastEvaluationDetails && !hasModifiedSinceEvaluation) {
      setHasModifiedSinceEvaluation(true);
    }
  }, [lastEvaluationDetails, hasModifiedSinceEvaluation]);

  const updateSentenceAnswer = useCallback(
    (sentenceIndex: number, updater: (current: string[]) => string[]) => {
      if (isReadOnly) return;
      markModified();
      setAnswers((prev) => {
        const newAnswers = [...prev];
        newAnswers[sentenceIndex] = updater([...(newAnswers[sentenceIndex] || [])]);
        return newAnswers;
      });
    },
    [isReadOnly, markModified]
  );

  const handleWordSelect = useCallback(
    (sentenceIndex: number, word: string) => {
      updateSentenceAnswer(sentenceIndex, (current) => {
        if (!current.includes(word)) current.push(word);
        return current;
      });
    },
    [updateSentenceAnswer]
  );

  const handleWordRemove = useCallback(
    (sentenceIndex: number, position: number) => {
      updateSentenceAnswer(sentenceIndex, (current) => {
        current.splice(position, 1);
        return current;
      });
    },
    [updateSentenceAnswer]
  );

  const handleReset = useCallback(
    (sentenceIndex: number) => {
      updateSentenceAnswer(sentenceIndex, () => []);
    },
    [updateSentenceAnswer]
  );

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!onExerciseSubmit) {
      return;
    }

    try {
      setIsSubmitting(true);

      await onExerciseSubmit(content, {
        type: ExerciseType.Scramble,
        answers,
      });

      setHasModifiedSinceEvaluation(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Check if any sentence has at least one word
  const hasAnyAnswer = answers.some((a) => a.length > 0);
  // Check if all sentences are complete
  const allComplete = sentences.every((sentence, idx) => {
    const words = sentence.correctSentence.trim().split(/\s+/).filter(w => w.length > 0);
    return (answers[idx]?.length ?? 0) === words.length;
  });

  // Get evaluation for a specific sentence
  const getEvaluationForSentence = (sentenceIndex: number) => {
    if (hasModifiedSinceEvaluation || !lastEvaluationDetails?.sentenceEvaluations) {
      return null;
    }
    return lastEvaluationDetails.sentenceEvaluations[sentenceIndex] ?? null;
  };

  return (
    <form
      onSubmit={handleSubmit}
      className={cn(
        'space-y-4 sm:space-y-6',
        variant === 'card' && 'rounded-xl sm:rounded-2xl border border-border bg-card p-4 sm:p-6 shadow-sm'
      )}
    >
      {content.title && (
        <h3 className="text-lg sm:text-xl font-semibold text-foreground">
          {renderInlineMarkdown(content.title, 'scramble-title')}
        </h3>
      )}
      <ContentDescription text={content.description} markdown />

      {/* Instructions */}
      <div className="rounded-lg sm:rounded-xl border border-dashed border-slate-300 bg-slate-50/50 px-3 py-2.5 sm:p-4">
        <p className="text-xs sm:text-sm text-slate-600 text-center">
          <span className="font-medium text-slate-700">Unscramble the words!</span>{' '}
          <span className="hidden sm:inline">Tap words from the word bank to arrange them in the correct order.</span>
          <span className="sm:hidden">Tap words to build sentences.</span>
        </p>
      </div>

      {/* Sentences */}
      <div className="space-y-6">
        {sentences.map((sentence, sentenceIndex) => (
          <SentenceScramble
            key={sentence.sentenceId}
            sentence={sentence}
            sentenceIndex={sentenceIndex}
            totalSentences={sentences.length}
            userAnswer={answers[sentenceIndex] || []}
            scrambledWords={scrambledWordsBySentence[sentenceIndex]}
            evaluation={getEvaluationForSentence(sentenceIndex)}
            isReadOnly={isReadOnly}
            onWordSelect={(word) => handleWordSelect(sentenceIndex, word)}
            onWordRemove={(position) => handleWordRemove(sentenceIndex, position)}
            onReset={() => handleReset(sentenceIndex)}
          />
        ))}
      </div>

      <ExerciseFeedbackList feedback={feedback ?? []} pending={isSubmitting} />

      {onExerciseSubmit && !isReadOnly && (
        <div className="flex justify-center pt-2">
          <Button
            type="submit"
            disabled={!hasAnyAnswer || isSubmitting}
            variant="submit"
            size="submit"
            isLoading={isSubmitting}
            loadingText="Submitting..."
            className="w-full sm:w-auto min-w-[200px]"
          >
            {allComplete ? 'Submit answer' : 'Submit partial answer'}
          </Button>
        </div>
      )}
    </form>
  );
}
